import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Save, RefreshCw, Loader2, ExternalLink, RotateCcw, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { IMAGE_SLOTS, IMAGE_CATEGORIES, SLOT_MAP } from "@/data/imageSlots";
import { setImageOverride, refreshImages } from "@/hooks/useImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminImagesPanel({ token }) {
  const [overrides, setOverrides] = useState({}); // {key: {url, alt, updated_at}}
  const [drafts, setDrafts] = useState({});        // {key: {url, alt}}
  const [activeCat, setActiveCat] = useState(IMAGE_CATEGORIES[0]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/images`, { headers });
      const map = {};
      (Array.isArray(data) ? data : []).forEach((d) => { if (d.key) map[d.key] = d; });
      setOverrides(map);
      setDrafts({});
      await refreshImages();
    } catch (e) {
      toast.error("Could not load images");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const slotsByCat = useMemo(() => {
    const groups = {};
    IMAGE_SLOTS.forEach((s) => {
      (groups[s.category] = groups[s.category] || []).push(s);
    });
    return groups;
  }, []);

  const currentForKey = (key) => {
    const o = overrides[key];
    if (drafts[key]) return drafts[key];
    if (o) return { url: o.url || "", alt: o.alt || "" };
    return { url: "", alt: "" };
  };

  const effectiveUrl = (slot) => {
    const o = overrides[slot.key];
    return (drafts[slot.key]?.url || o?.url || slot.defaultUrl);
  };

  const setDraft = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...currentForKey(key), ...patch } }));

  const isUrlValid = (s) => {
    if (!s) return true;
    try {
      const u = new URL(s);
      return u.protocol === "http:" || u.protocol === "https:" || u.protocol === "data:";
    } catch (e) { return false; }
  };

  const save = async (slot) => {
    const draft = currentForKey(slot.key);
    if (!isUrlValid(draft.url)) { toast.error("Please paste a valid http(s) URL or leave it blank."); return; }
    setSavingKey(slot.key);
    try {
      const payload = {
        url: draft.url.trim(),
        alt: (draft.alt || "").trim(),
        category: slot.category,
        label: slot.label,
      };
      const { data } = await axios.put(`${API}/admin/images/${encodeURIComponent(slot.key)}`, payload, { headers });
      setOverrides((o) => ({ ...o, [slot.key]: data }));
      setDrafts((d) => { const n = { ...d }; delete n[slot.key]; return n; });
      setImageOverride(slot.key, data.url ? { url: data.url, alt: data.alt } : null);
      toast.success(`✓ ${slot.label} updated. The change is live on the website.`, {
        description: "Open the page in a new tab to verify. If you still see the old image, hard-refresh (Ctrl+Shift+R / Cmd+Shift+R) — it's a browser cache, not the site.",
        duration: 6000,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const resetToDefault = async (slot) => {
    if (!window.confirm(`Reset "${slot.label}" to the default image? This removes your custom override.`)) return;
    setSavingKey(slot.key);
    try {
      await axios.delete(`${API}/admin/images/${encodeURIComponent(slot.key)}`, { headers });
      setOverrides((o) => { const n = { ...o }; delete n[slot.key]; return n; });
      setDrafts((d) => { const n = { ...d }; delete n[slot.key]; return n; });
      setImageOverride(slot.key, null);
      toast.success(`Reset: ${slot.label}`);
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setSavingKey(null);
    }
  };

  const total = IMAGE_SLOTS.length;
  const overriddenCount = Object.keys(overrides).filter((k) => overrides[k]?.url && SLOT_MAP[k]).length;

  return (
    <div data-testid="admin-images-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="text-[13px] text-[#3B312D]/70">
            {total} image slots · {overriddenCount} customised
          </p>
        </div>
        <button onClick={load} disabled={loading} className="btn-ghost" data-testid="admin-images-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {/* Helper tip — Cloudinary guidance */}
      <div className="lux-card p-5 mb-7 text-[13.5px] text-[#3B312D]/80 leading-relaxed bg-white">
        <p className="font-semibold text-[#3B312D] mb-1.5 inline-flex items-center gap-2">
          <ImagePlus size={15} className="text-[#B65980]" /> How to update an image
        </p>
        <p>
          Paste any public image URL (Cloudinary, your own CDN, customer-assets, or an Unsplash direct image link).
          For uploads, drop your file into a Cloudinary media library, copy its delivery URL, and paste here. You can
          revert any slot to its default at any time.
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-7" data-testid="admin-images-categories">
        {IMAGE_CATEGORIES.map((c) => {
          const n = (slotsByCat[c] || []).length;
          if (n === 0 && c !== "Venues") return null;
          return (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`admin-images-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase font-semibold border transition ${activeCat === c ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]" : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.14)] hover:border-[#B65980]"}`}
            >
              {c}{n > 0 ? ` (${n})` : ""}
            </button>
          );
        })}
      </div>

      <div className="space-y-5">
        {activeCat === "Venues" && (
          <div className="lux-card p-6 bg-[#F4DCD8]/40 text-[14px] text-[#3B312D]/85 leading-relaxed" data-testid="admin-images-venues-note">
            <p className="font-semibold text-[#3B312D] mb-1.5">Venue images are managed in the Venues tab</p>
            <p>
              Each venue&apos;s photo lives on the venue record itself. Open the{" "}
              <strong>Venues</strong> tab above to add, edit, and replace venue images — those updates
              flow automatically to both the <strong>Venues page</strong> and the <strong>homepage venue preview</strong>.
            </p>
          </div>
        )}
        {(slotsByCat[activeCat] || []).map((slot) => {
          const draft = currentForKey(slot.key);
          const dirty = !!drafts[slot.key];
          const valid = isUrlValid(draft.url);
          const previewUrl = effectiveUrl(slot);
          const overridden = !!overrides[slot.key]?.url;
          return (
            <div key={slot.key} className="lux-card p-5 grid grid-cols-1 md:grid-cols-12 gap-5" data-testid={`admin-image-${slot.key}`}>
              <div className="md:col-span-3 aspect-[4/3] rounded-xl overflow-hidden bg-[#F4DCD8]/40 self-start">
                {previewUrl ? (
                  <img src={previewUrl} alt={slot.label} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#3B312D]/40 text-xs">No preview</div>
                )}
              </div>
              <div className="md:col-span-9 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold">{slot.category}</p>
                    <h3 className="font-display text-xl text-[#3B312D] mt-1">{slot.label}</h3>
                    <p className="text-[11.5px] text-[#3B312D]/55 mt-1 font-mono break-all">{slot.key}</p>
                  </div>
                  <span className={`pill !text-[10px] !py-1 !px-3 ${overridden ? "" : "!bg-[#3B312D]/8 !border-[#3B312D]/14 !text-[#3B312D]/55"}`}>
                    {overridden ? "Custom" : "Default"}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  <label className="block">
                    <span className="block text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold mb-1.5">Image URL (Cloudinary / public)</span>
                    <input
                      type="url"
                      inputMode="url"
                      placeholder={slot.defaultUrl}
                      value={draft.url}
                      onChange={(e) => setDraft(slot.key, { url: e.target.value })}
                      className="lux-input"
                      data-testid={`admin-image-url-${slot.key}`}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold mb-1.5">Alt text (for SEO &amp; accessibility)</span>
                    <input
                      type="text"
                      placeholder={slot.defaultAlt}
                      value={draft.alt}
                      onChange={(e) => setDraft(slot.key, { alt: e.target.value })}
                      className="lux-input"
                      data-testid={`admin-image-alt-${slot.key}`}
                    />
                  </label>
                </div>

                {!valid && (
                  <p className="mt-2 text-[12px] text-[#B65980]">That URL doesn&apos;t look right — must start with https://.</p>
                )}

                <div className="mt-4 flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => save(slot)}
                    disabled={!dirty || !valid || savingKey === slot.key}
                    className="btn-primary !py-2 !px-4 !text-[11px]"
                    data-testid={`admin-image-save-${slot.key}`}
                  >
                    {savingKey === slot.key ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Save
                  </button>
                  {overridden && (
                    <button
                      onClick={() => resetToDefault(slot)}
                      disabled={savingKey === slot.key}
                      className="btn-ghost !py-2 !px-4 !text-[11px]"
                      data-testid={`admin-image-reset-${slot.key}`}
                    >
                      <RotateCcw size={12} /> Reset to default
                    </button>
                  )}
                  {draft.url && (
                    <a href={draft.url} target="_blank" rel="noreferrer noopener" className="text-[11px] tracking-[0.18em] uppercase text-[#B65980] hover:underline font-semibold inline-flex items-center gap-1">
                      Open <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-[12.5px] text-[#3B312D]/70 leading-relaxed max-w-2xl">
        <p className="font-semibold text-[#3B312D] mb-2">Want to make more images editable?</p>
        <p>
          Add a new entry to <code className="font-mono text-[#B65980]">/app/frontend/src/data/imageSlots.js</code>{" "}
          and swap the corresponding <code className="font-mono">&lt;img /&gt;</code> for{" "}
          <code className="font-mono">&lt;Img imageKey=&quot;your.key&quot; defaultSrc=&quot;…&quot; /&gt;</code>.
          The new slot will appear here automatically.
        </p>
      </div>
    </div>
  );
}
